/**
 * Pantalla de pausa del tenant (Sprint 25-2, mockups M1/M1b ronda 2.1).
 *
 * `OnboardingGate` la renderiza a pantalla completa (sin `AppShell`)
 * cuando `context.tenant_status !== 'active'` — el usuario no tiene
 * acceso operativo al portal mientras dure la pausa, así que no tiene
 * sentido mostrarle el sidebar/nav de una app a la que no puede entrar.
 *
 * Tres variantes según `tenant_status` + `subscription_state` (Ruling
 * de la Task 8, ver brief):
 *
 * - `pending_deletion` → borrado programado. Badge rojo, CTA esmeralda
 *   (no rojo — cancelar el borrado es la acción que PROTEGE los datos,
 *   el rojo queda solo en el badge que informa el riesgo).
 * - `inactive` + `subscription_state` en {hibernation, cancelled,
 *   awaiting_payment} → pausa por falta de renovación. Badge ámbar, CTA
 *   a `/membresia/renovar`.
 * - Cualquier otro `inactive` (pausa operativa decidida por soporte, no
 *   por facturación) → badge azul, sin CTA de renovación.
 *
 * Los datos reales del contexto (tier, cantidad de miembros) se usan
 * cuando están disponibles; si `seats_used` no llegó, la card de "tu
 * información te espera intacta" cae a una versión genérica sin
 * inventar una cifra.
 *
 * **Salida (pie común a las 3 variantes, fix final F1).** Esta pantalla
 * reemplaza al `AppShell` completo, y con él al único botón "Cerrar
 * sesión" que tiene un usuario con tenant: `/login` y `/` rebotan una
 * sesión viva a `/radar`, y `/radar` vuelve acá. En un computador
 * compartido de la alcaldía eso dejaba a la persona pausada "atrapada"
 * y a cualquier otra sin poder entrar con su cuenta. Por eso el pie
 * ofrece siempre "Cerrar sesión" (`useAuth().signOut`, el mismo que
 * usa `AppShell`) y, si el usuario es staff de Kaptar, "Ir al panel
 * admin" — un tenant pausado no le bloquea el panel, solo el portal.
 */
import type { ReactNode } from "react";
import { useEffect } from "react";
import { Link } from "react-router-dom";

import type { OnboardingContextDTO } from "@/lib/api";
import { track } from "@/lib/analytics";
import { useAuth } from "@/lib/auth";

const SOPORTE_MAILTO = "mailto:soporte@kaptar.com.co";

/** `subscription_state` que cuentan como "pausado por no renovar" (billing). */
const RENOVACION_SUBSCRIPTION_STATES = new Set(["hibernation", "cancelled", "awaiting_payment"]);

const TIER_LABELS: Record<string, string> = {
  esencial: "Esencial",
  pro: "Pro",
};

type Variante = "borrado" | "renovacion" | "operativa";

// Telemetría (Sprint 25-6): `membresia_pausa_viewed` usa nombres de
// variante distintos a los internos -- "borrado" es el nombre corto
// que usa este archivo, pero la property pública que consume el
// instructivo de pruebas es `pending_deletion` (el valor real de
// `tenant_status`, más legible en el dashboard de analytics que el
// alias interno).
const VARIANTE_EVENTO: Record<Variante, "pending_deletion" | "renovacion" | "operativa"> = {
  borrado: "pending_deletion",
  renovacion: "renovacion",
  operativa: "operativa",
};

function resolverVariante(context: OnboardingContextDTO): Variante {
  if (context.tenant_status === "pending_deletion") return "borrado";
  if (
    context.tenant_status === "inactive" &&
    context.subscription_state &&
    RENOVACION_SUBSCRIPTION_STATES.has(context.subscription_state)
  ) {
    return "renovacion";
  }
  return "operativa";
}

function nombreMunicipio(context: OnboardingContextDTO): string {
  return context.active_municipality_name || context.active_tenant_display_name || "Tu municipio";
}

function tierLabel(tier: string | null | undefined): string | null {
  if (!tier) return null;
  return TIER_LABELS[tier] ?? tier;
}

function mailtoCancelarBorrado(muni: string): string {
  const subject = `Cancelar borrado programado — ${muni}`;
  return `${SOPORTE_MAILTO}?subject=${encodeURIComponent(subject)}`;
}

export function TenantPausado({ context }: { context: OnboardingContextDTO }) {
  const { signOut } = useAuth();
  const variante = resolverVariante(context);
  const muni = nombreMunicipio(context);
  const esStaff = context.is_staff === true;

  // Telemetría -- 1 fire por mount. Dep en `variante` (no en `context`,
  // que cambia de referencia en cada render del padre) para no
  // duplicar el evento en un re-render que no cambia la variante
  // mostrada.
  useEffect(() => {
    track({
      event_name: "membresia_pausa_viewed",
      properties: { variante: VARIANTE_EVENTO[variante] },
    });
  }, [variante]);

  return (
    <div
      data-testid="tenant-pausado"
      className="flex min-h-screen items-center justify-center bg-surface px-4 py-10"
    >
      <div className="w-full max-w-md">
        <header className="mb-6 flex items-center justify-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-600">
            <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden="true">
              <path d="M12 3L4 8l8 5 8-5-8-5z" fill="white" opacity="0.9" />
              <path d="M4 13l8 5 8-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <h1 className="text-lg font-extrabold tracking-tight text-slate-900">Kaptar</h1>
        </header>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs sm:p-8">
          {variante === "borrado" && <VarianteBorrado muni={muni} />}
          {variante === "renovacion" && <VarianteRenovacion muni={muni} context={context} />}
          {variante === "operativa" && <VarianteOperativa muni={muni} />}
        </div>

        <PieSalida esStaff={esStaff} onSignOut={signOut} />
      </div>
    </div>
  );
}

// --- Pie de salida (común a las 3 variantes) ---------------------------

/**
 * Ver "Salida" en el docstring del módulo. Va FUERA de la card blanca:
 * no es parte del mensaje de la pausa sino la salida de emergencia de
 * la pantalla. Botón (no `<a>`) para "Cerrar sesión" porque es una
 * acción, no una navegación — igual que en `AppShell`.
 */
function PieSalida({ esStaff, onSignOut }: { esStaff: boolean; onSignOut: () => Promise<void> }) {
  return (
    <nav
      aria-label="Salir de la cuenta"
      className="mt-4 flex flex-wrap items-center justify-center gap-x-1 text-xs text-slate-500"
    >
      {esStaff && (
        <>
          <Link
            to="/admin"
            className="inline-flex min-h-11 items-center px-2 font-semibold text-brand-700 hover:underline"
          >
            Ir al panel admin
          </Link>
          <span aria-hidden="true">·</span>
        </>
      )}
      <button
        type="button"
        onClick={() => {
          void onSignOut();
        }}
        className="inline-flex min-h-11 items-center px-2 font-semibold text-slate-600 hover:underline"
      >
        Cerrar sesión
      </button>
    </nav>
  );
}

// --- Piezas compartidas -----------------------------------------------

function Badge({
  tone,
  icon,
  children,
}: {
  tone: "warning" | "danger" | "info";
  icon: ReactNode;
  children: ReactNode;
}) {
  const toneClasses: Record<typeof tone, string> = {
    warning: "bg-amber-100 text-amber-700",
    danger: "bg-red-100 text-red-700",
    info: "bg-brand-100 text-brand-700",
  };
  return (
    <div className="mb-3 text-center">
      <span
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold tracking-wide uppercase ${toneClasses[tone]}`}
      >
        {icon}
        {children}
      </span>
    </div>
  );
}

function Titulo({ children }: { children: ReactNode }) {
  return (
    <h2 className="mb-2 text-center text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
      {children}
    </h2>
  );
}

function Sub({ children }: { children: ReactNode }) {
  return (
    <p className="mx-auto mb-5 max-w-sm text-center text-sm leading-relaxed text-slate-600">
      {children}
    </p>
  );
}

function CtaSolido({ href, children }: { href: string; children: ReactNode }) {
  return (
    <Link
      to={href}
      className="block w-full rounded-md bg-cta-600 px-4 py-2.5 text-center text-sm font-semibold text-white transition-colors hover:bg-cta-700"
    >
      {children}
    </Link>
  );
}

function CtaMailto({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      className="block w-full rounded-md bg-cta-600 px-4 py-2.5 text-center text-sm font-semibold text-white transition-colors hover:bg-cta-700"
    >
      {children}
    </a>
  );
}

function GhostSoporte({ acento = false }: { acento?: boolean }) {
  return (
    <a
      href={SOPORTE_MAILTO}
      className={
        "block w-full rounded-md border px-4 py-2.5 text-center text-sm font-semibold text-brand-700 transition-colors hover:bg-brand-50 " +
        (acento ? "border-brand-600" : "border-slate-300")
      }
    >
      Contactar soporte
    </a>
  );
}

function Foot({ children }: { children: ReactNode }) {
  return <p className="mt-3 text-center text-xs text-slate-500">{children}</p>;
}

// --- Iconos --------------------------------------------------------------

function PauseIcon() {
  return (
    <svg
      className="h-3 w-3"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      aria-hidden="true"
    >
      <rect x="6" y="4" width="4" height="16" rx="1" />
      <rect x="14" y="4" width="4" height="16" rx="1" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg
      className="h-3 w-3"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v4M12 16h.01" />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg
      className="h-3 w-3"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      aria-hidden="true"
    >
      <path d="M12 9v4M12 17h.01M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      className="mt-0.5 h-4 w-4 shrink-0 text-cta-600"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M20 6L9 17l-5-5" />
    </svg>
  );
}

// --- Variante: renovación (badge ámbar) -----------------------------------

function VarianteRenovacion({ muni, context }: { muni: string; context: OnboardingContextDTO }) {
  const tier = tierLabel(context.tier);
  const seatsUsed = context.seats_used;

  return (
    <>
      <Badge tone="warning" icon={<PauseIcon />}>
        Membresía en pausa
      </Badge>
      <Titulo>{muni} está en pausa por renovación</Titulo>
      <Sub>
        {tier ? (
          <>
            El período de tu membresía <strong>{tier}</strong> terminó y todavía no registramos la
            renovación. El acceso queda suspendido mientras tanto —{" "}
            <strong>nada se ha borrado</strong>.
          </>
        ) : (
          <>
            El período de tu membresía terminó y todavía no registramos la renovación. El acceso
            queda suspendido mientras tanto — <strong>nada se ha borrado</strong>.
          </>
        )}
      </Sub>

      <div className="mb-5 rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="mb-3 text-xs font-semibold tracking-wide text-slate-400 uppercase">
          Tu información te espera intacta
        </h3>
        <ul className="flex flex-col gap-2.5">
          <li className="flex items-start gap-2 text-sm text-slate-600">
            <CheckIcon />
            <span>Tus expedientes con sus documentos y su historial completo</span>
          </li>
          <li className="flex items-start gap-2 text-sm text-slate-600">
            <CheckIcon />
            <span>El registro de convocatorias descubiertas y su trazabilidad</span>
          </li>
          <li className="flex items-start gap-2 text-sm text-slate-600">
            <CheckIcon />
            <span>
              {/* Con 1 no basta singularizar el sustantivo: "los 1 miembro" (#732). */}
              {seatsUsed === 1 ? (
                <>
                  La cuenta del <strong>único miembro</strong> del equipo
                </>
              ) : seatsUsed ? (
                <>
                  Las cuentas de los <strong>{seatsUsed} miembros</strong> del equipo
                </>
              ) : (
                "Las cuentas de los miembros del equipo"
              )}
            </span>
          </li>
        </ul>
      </div>

      <div className="flex flex-col gap-2">
        <CtaSolido href="/membresia/renovar">Renovar la membresía</CtaSolido>
        <GhostSoporte />
      </div>
      <Foot>¿Crees que es un error? Escríbenos.</Foot>
    </>
  );
}

// --- Variante: operativa (badge azul, sin CTA de renovación) -------------

function VarianteOperativa({ muni }: { muni: string }) {
  return (
    <>
      <Badge tone="info" icon={<InfoIcon />}>
        En pausa
      </Badge>
      <Titulo>{muni} está pausado temporalmente</Titulo>
      <Sub>
        El equipo de Kaptar pausó el acceso de este municipio. No es un tema de pago y tus datos
        están intactos.
      </Sub>
      <GhostSoporte acento />
    </>
  );
}

// --- Variante: borrado programado (badge rojo, CTA esmeralda) ------------

function VarianteBorrado({ muni }: { muni: string }) {
  return (
    <>
      <Badge tone="danger" icon={<WarningIcon />}>
        Eliminación programada
      </Badge>
      <Titulo>{muni} tiene un borrado programado</Titulo>
      <Sub>
        El borrado es <strong>definitivo</strong>: expedientes, documentos y miembros. Si es un
        error, aún está a tiempo de cancelarse.
      </Sub>
      <CtaMailto href={mailtoCancelarBorrado(muni)}>Cancelar el borrado</CtaMailto>
      <Foot>Te conecta con soporte — lo cancelamos antes de la fecha.</Foot>
    </>
  );
}
